/**
 * Shared contract types and helpers for the AI Assistant pages — one copy of
 * the API shapes (mirroring the V3 assistant routes) so list, view and edit
 * pages cannot drift apart.
 */

export const SCOPE = { user: 'user', global: 'global' } as const;
export type SkillScope = (typeof SCOPE)[keyof typeof SCOPE];

export const AUDIENCE = {
  internal: 'internal',
  internalAndContractors: 'internal-and-contractors',
} as const;
export type SkillAudience = (typeof AUDIENCE)[keyof typeof AUDIENCE];

export const AUDIENCE_LABELS: Readonly<Record<SkillAudience, string>> = {
  [AUDIENCE.internal]: 'Internal only',
  [AUDIENCE.internalAndContractors]: 'Internal + contractors',
};

/** One row of the skills index. */
export interface SkillEntry {
  readonly name: string;
  readonly scope: SkillScope;
  readonly audience: SkillAudience;
  readonly description: string;
  readonly version: number;
}

/** One skill in full. */
export interface SkillFile extends SkillEntry {
  readonly instructions: string;
  readonly tools: readonly string[];
}

/** One historical version of a skill. */
export interface SkillVersionEntry {
  readonly version: number;
  readonly description: string;
  readonly instructions: string;
  readonly tools: readonly string[];
  readonly updatedAt: string | null;
}

export interface SkillsResponse {
  readonly skills: readonly SkillEntry[];
  readonly canManageGlobal: boolean;
}

/** One memory file as the index lists it. */
export interface MemoryIndexEntry {
  readonly id: string;
  readonly path: string;
  readonly description: string;
  readonly version: number;
  readonly updatedAt: string | null;
}

/** One memory file in full. */
export interface MemoryFile extends MemoryIndexEntry {
  readonly content: string;
}

/** One historical version of a memory file. */
export interface MemoryVersionEntry {
  readonly version: number;
  readonly content: string;
  readonly updatedBy: string;
  readonly createdAt: string | null;
}

/** "Mar 4 · 2:15 PM", or null without a timestamp. */
export function whenLabel(updatedAt: string | null): string | null {
  if (updatedAt === null) return null;
  const date = new Date(updatedAt);
  return (
    date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' · ' +
    date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  );
}

/** A small toggle chip (version history, etc.). */
export function chipClass(active: boolean): string {
  return [
    'cursor-pointer rounded-portal-pill border px-2.5 py-0.5 text-portal-micro font-bold transition-colors',
    active
      ? 'border-brand bg-portal-sky text-brand'
      : 'border-portal-line text-portal-muted hover:border-brand hover:text-brand',
  ].join(' ');
}

/** Parses a route's scope param, defaulting to personal. */
export function parseScope(value: string | undefined): SkillScope {
  return value === SCOPE.global ? SCOPE.global : SCOPE.user;
}

/** One skill schedule, as the API returns it. */
export interface ScheduleRunTime {
  readonly cron: string;
  readonly timezone: string;
}

export interface ScheduleRecord {
  readonly id: string;
  readonly skillName: string;
  readonly skillScope: SkillScope;
  readonly name: string;
  readonly cron: string;
  readonly timezone: string;
  readonly runTimes: readonly ScheduleRunTime[];
  readonly parameters: string;
  readonly enabled: boolean;
  readonly nextRunAt: string;
  readonly lastRunAt: string | null;
  readonly lastStatus: string | null;
  readonly consecutiveFailures: number;
  readonly version: number;
}

/** Preset cadences the schedule builder offers; 'custom' = raw cron. */
export const CADENCE_PRESETS = {
  daily: 'daily',
  weekdays: 'weekdays',
  weekly: 'weekly',
  custom: 'custom',
} as const;
export type CadencePreset = (typeof CADENCE_PRESETS)[keyof typeof CADENCE_PRESETS];

export const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

/** Compiles a preset + time into a 5-field cron expression. */
export function presetToCron(
  preset: CadencePreset,
  time: string,
  weekday: number,
  custom: string,
): string {
  if (preset === CADENCE_PRESETS.custom) return custom;
  const [hRaw, mRaw] = time.split(':');
  const h = Number(hRaw ?? 0);
  const m = Number(mRaw ?? 0);
  switch (preset) {
    case CADENCE_PRESETS.daily:
      return `${m} ${h} * * *`;
    case CADENCE_PRESETS.weekdays:
      return `${m} ${h} * * 1-5`;
    default:
      return `${m} ${h} * * ${weekday}`;
  }
}

/** Best-effort inverse of presetToCron, for editing an existing schedule. */
export function cronToPreset(cron: string): {
  preset: CadencePreset;
  time: string;
  weekday: number;
} {
  const pad = (n: string) => n.padStart(2, '0');
  let match = /^(\d{1,2}) (\d{1,2}) \* \* \*$/.exec(cron);
  if (match?.[1] !== undefined && match[2] !== undefined) {
    return { preset: CADENCE_PRESETS.daily, time: `${pad(match[2])}:${pad(match[1])}`, weekday: 1 };
  }
  match = /^(\d{1,2}) (\d{1,2}) \* \* 1-5$/.exec(cron);
  if (match?.[1] !== undefined && match[2] !== undefined) {
    return { preset: CADENCE_PRESETS.weekdays, time: `${pad(match[2])}:${pad(match[1])}`, weekday: 1 };
  }
  match = /^(\d{1,2}) (\d{1,2}) \* \* (\d)$/.exec(cron);
  if (match?.[1] !== undefined && match[2] !== undefined && match[3] !== undefined) {
    return {
      preset: CADENCE_PRESETS.weekly,
      time: `${pad(match[2])}:${pad(match[1])}`,
      weekday: Number(match[3]),
    };
  }
  return { preset: CADENCE_PRESETS.custom, time: '09:00', weekday: 1 };
}

/** Timezones the schedule builder offers; Eastern first as the BCE default. */
export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern (EST/EDT)' },
  { value: 'America/Chicago', label: 'Central (CST/CDT)' },
  { value: 'America/Denver', label: 'Mountain (MST/MDT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PST/PDT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKST/AKDT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'UTC', label: 'UTC' },
] as const;

export const DEFAULT_TIMEZONE = 'America/New_York';

/** One recorded run of a schedule, newest first from the API. */
export interface ScheduleRunRecord {
  readonly status: 'ok' | 'error' | 'skipped';
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly output: string;
  readonly actions: ReadonlyArray<{ readonly tool: string; readonly ok: boolean; readonly detail?: string }>;
  readonly inputTokens: number | null;
  readonly outputTokens: number | null;
  readonly error: string | null;
  readonly skillName: string;
}
